import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import StravaSyncModal from "../components/StravaSyncModal";

const StravaModalContext = createContext(null);

export const StravaModalProvider = ({ children }) => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    initialTab: "sync",
    refreshKey: 0,
  });
  const listenersRef = useRef([]);

  const openModal = useCallback((options = {}) => {
    setModalState((prev) => ({
      isOpen: true,
      initialTab: options.tab || "sync",
      refreshKey: prev.refreshKey + 1,
    }));
  }, []);

  const closeModal = useCallback(() => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const registerStatusListener = useCallback((listener) => {
    if (typeof listener !== "function") {
      return () => {};
    }
    listenersRef.current.push(listener);
    return () => {
      listenersRef.current = listenersRef.current.filter(
        (registered) => registered !== listener
      );
    };
  }, []);

  const notifyStatusListeners = useCallback((payload) => {
    listenersRef.current.forEach((listener) => {
      try {
        listener(payload);
      } catch (error) {
        console.error("Error notifying Strava status listener:", error);
      }
    });
  }, []);

  const value = useMemo(
    () => ({
      openModal,
      closeModal,
      registerStatusListener,
    }),
    [openModal, closeModal, registerStatusListener]
  );

  return (
    <StravaModalContext.Provider value={value}>
      {children}
      <StravaSyncModal
        key={modalState.refreshKey}
        isOpen={modalState.isOpen}
        initialTab={modalState.initialTab}
        onRequestClose={closeModal}
        onStatusUpdate={notifyStatusListeners}
      />
    </StravaModalContext.Provider>
  );
};

export const useStravaModal = () => {
  const context = useContext(StravaModalContext);
  if (!context) {
    throw new Error("useStravaModal must be used within StravaModalProvider");
  }
  return context;
};
